import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../../common_nextgen/axios/AxiosHelper";
import { IndicatorCell } from "../../../common_nextgen/common_components/IndicatorCell/IndicatorCell";
import useCustomNavigate from "../../../common_nextgen/common_components/Navigation/NextGenNavigation";
import { fetchDropdownValues } from "../../../common_nextgen/common_components/dropdown/DropdownHelper";
import { useToast } from "../../../common_nextgen/hooks/ToastContext";
import { ROUTE_PATHS } from "../../../common_nextgen/utils/Constants";
import { downloadCSV } from "../../../common_nextgen/utils/UtilFunctions";

const getSelectedSteps = (selectedRows, rowData) => (Array.isArray(selectedRows) && selectedRows.length) ? selectedRows : [rowData];

const useGPICouponPatientPay = () => {
  const { axios_helper_no_header } = useAxios();
  const location = useLocation();
  const [stateVal, setStateVal] = useState(location.state || []);
  const { currentState, previousState } = useMemo(() => {
    let [currentState = {}] = [...stateVal].reverse();
    const previousState = [...stateVal];
    previousState.splice(stateVal.length - 1, 1);
    return { previousState, currentState };
  }, [stateVal]);

  const {
    selectedSchedules = [],
    previousScreen = ROUTE_PATHS.COUPON_PATIENT_PAY,
    currentIndex: initialIndex = 0,
    externalMode: initialExternalMode = false,
    paramsData: initialParamsData = {
      cpfGpiListName: selectedSchedules[initialIndex]?.cpfGpiListName,
      cpfGpiListDescription: selectedSchedules[initialIndex]?.cpfGpiListDescription,
      cpgGenericProductId: "",
      hiddenRecordIndicator: "A"
    }
  } = currentState;

  const [externalMode, setExternalMode] = useState(initialExternalMode);
  const [paramsData, setParamsData] = useState(initialParamsData);
  const [data, setData] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowData, setRowData] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [dropdownValues, setDropdownValues] = useState({});
  const navigateToPage = useCustomNavigate();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("no");
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isInactiveConfirmationModalOpen, setIsInactiveConfirmationModalOpen] = useState(false);
  const [inactiveSelectedOption, setInactiveSelectedOption] = useState("no");
  const toast = useToast();
  const {
    ADD_COUPON_PATIENT_PAY_GPI_LIST,
    EDIT_COUPON_PATIENT_PAY_GPI_LIST,
    DISPLAY_COUPON_PATIENT_PAY_GPI_LIST,
    INACTIVE_GPI_COUPON_PATIENT_PAY,
    NOTE_GPI_COUPON_PATIENT_PAY,
  } = ROUTE_PATHS;

  const screen_title = "GPIs in Coupon Patient Pay GPI List";
  const screenNumber = "RCCPG001";

  const formFields = [
    {
      fieldLabel: "CPF GPI List Name",
      name: "cpfGpiListName",
      label: "List",
      type: "text",
      fieldNature: "readOnly",
      value: "",
      maxLength: 10,
    },
    {
      fieldLabel: "CPF GPI List Description",
      name: "cpfGpiListDescription",
      label: "Description",
      type: "text",
      fieldNature: "readOnly",
      value: "",
      maxLength: 30,
    },
    {},
    {
      fieldLabel: "CPG Generic Product ID",
      name: "cpgGenericProductId",
      label: "GPI",
      type: "text",
      fieldNature: "editable",
      value: "",
      maxLength: 14,
    },
    {
      fieldLabel: "Hidden Records Indicator*Inactive",
      name: "hiddenRecordIndicator",
      label: "Status",
      type: "dropdown",
      fieldNature: "editable",
      maxLength: 20,
    },
  ];

  const dropdownOptions = [
    { field_label: "Hidden Records Indicator", condition: "*Inactive" },
  ];

  const dropdownCondition = {
    dropDowns: dropdownOptions.map((item) => item.field_label + item.condition),
  };

  const handleBack = () => {
    if (externalMode) {
      const message = {
        message: "navigate-to-external-program",
        url: previousScreen,
        parameters: { state: [...previousState] }
      };
      window.parent.postMessage(message, "*");
    } else if (currentIndex < selectedSchedules.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    } else {
      navigateToPage(previousScreen, previousState);
    }
  };

  const leftButtons = [{ button_label: "Back", button_handeler: handleBack, button_key: "F12" }];

  const ctaButtons = externalMode ? [{ button_label: "Export as CSV", button_key: "", button_handeler: () => exportAsCSV() }]: [{
    button_label: "Add GPIs in Coupon Patient Pay GPI List",
    button_handeler: () => {
      navigateToPage(ADD_COUPON_PATIENT_PAY_GPI_LIST, [
        ...previousState,
        {
          ...currentState,
          currentIndex,
          paramsData: paramsData,
        },
        {
          selectedSteps: selectedRows.length ? selectedRows : [rowData],
          selectedSchedules: [selectedSchedules[currentIndex]],
          previousScreen: location.pathname,
          currentIndex: 0,
        },
      ]);
    },
    button_key: "F6",
  },
  { button_label: "Export as CSV", button_key: "", button_handeler: () => exportAsCSV() }
]

  const menuOptions = [
    {
      label: "Note",
      icon: <span className="material-icons">note</span>,
      command: () => {
        const steps = getSelectedSteps(selectedRows, rowData);
        navigateToPage(NOTE_GPI_COUPON_PATIENT_PAY, [
          ...previousState,
          {
            ...currentState,
            currentIndex,
            externalMode,
            paramsData: {
              ...paramsData,
              cpgGenericProductId: steps.at(-1).cpgGenericProductId,
            },
          },
          {
            selectedPriceSchedules: steps,
            selectedSchedules: [selectedSchedules[currentIndex]],
            previousScreen: location.pathname,
            paramsData
          },
        ]);
      },
    },
    ...!externalMode && [
      {
        label: "Edit",
        icon: <span className="material-icons">edit</span>,
        command: () => {
          editValidation();
        }
      },
      {
        label: "Inactive",
        icon: <span className="material-icons">block</span>,
        command: () => {
          openInactiveConfirmationModal();
        },
      },
    ] || [],
    {
      label: "Display",
      icon: <span className="material-icons">visibility</span>,
      command: () => {
        const steps = getSelectedSteps(selectedRows, rowData);
        navigateToPage(DISPLAY_COUPON_PATIENT_PAY_GPI_LIST, [
          ...previousState,
          {
            ...currentState,
            currentIndex,
            externalMode,
            paramsData: {
              ...paramsData,
              cpgGenericProductId: steps.at(-1).cpgGenericProductId,
            },
          },
          {
            selectedSteps: steps,
            selectedSchedules: [selectedSchedules[currentIndex]],
            previousScreen: location.pathname,
            currentIndex: 0,
          },
        ]);
      },
    },
  ];

  const columnDef = [
    {
      header: "GPI",
      field: "cpgGenericProductId",
    },
    {
      header: "Name",
      field: "gpiName",
    },
    {
      header: "From Date",
      field: "cpgFromDate",
    },
    {
      header: "Thru Date",
      field: "cpgThruDate",
    },
    {
      header: "Indicators",
      field: "noteExistenceStatus",
      body: (rowData) => (
        <IndicatorCell rowData={rowData} fields={["noteExistenceStatus"]} tooltipContent={{ noteExistenceStatus: "Notes" }} />
      ),
    },
    {
      header: "Status",
      field: "hiddenRecordIndicator",
    },
  ];

  const parmKey = "recordId";
  const parmKey2 = "cpgSequence";

  const fetchData = async () => {
    const page = first / rows;
    try {
      const raw_response = await axios_helper_no_header(
        "GET",
        `api/cpgCouponPatPayGpiDtl`,
        null,
        {
          page: page,
          size: rows,
          ...paramsData,
        }
      );
      const response = raw_response.responseData;
      const pageResponse = raw_response.responsePaginationDTO;
      console.log(pageResponse);
      if (response.length === 0) {
        setData([]);
        setTotalRecords(0);
        toast.current.show({
          severity: "info",
          summary: "",
          detail: response.responseStatus.message,
          life: 3000,
        });
      } else {
        setData(response);
        setTotalRecords(pageResponse.totalRecords);
        showToast(raw_response);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setTotalRecords(0);
    }


  };

  const showToast = (raw_response) => {
    // Uncomment this part to show success toast
    toast.current.show({
      severity: "success",
      summary: "",
      detail: raw_response.responseStatus.message,
      life: 3000,
    });
  }

  const fetchDropdownData = async (dataDropdown) => {
    try {
      const response = await fetchDropdownValues(dataDropdown);
      setDropdownValues(response);
    } catch (error) {
      console.error("Error fetching dropdown values:", error);
    }
  };

  useEffect(() => {
    fetchDropdownData(dropdownCondition);
  }, []);

  useEffect(() => {
     if (paramsData?.cpfGpiListName) fetchData();
  }, [paramsData, rows, currentPage]);

  useEffect(() => {
    if (
      currentIndex !== initialIndex
    ) {
      setParamsData({
        cpfGpiListName: selectedSchedules[currentIndex]?.cpfGpiListName,
        cpfGpiListDescription:
          selectedSchedules[currentIndex]?.cpfGpiListDescription,
        cpgGenericProductId: selectedSchedules[currentIndex]?.cpgGenericProductId,
        hiddenRecordIndicator: "A",
      });
    }
  }, [currentIndex, initialIndex]);

  const onPageChange = (event) => {
    setFirst(event.first);
    setCurrentPage(event.page);
    setRows(event.rows);
  };

  const openConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setSelectedOption("no");
  };

  const exportAsCSV = async () => {
    const queryParams = new URLSearchParams({ ...paramsData });

    try {
      const response = await axios_helper_no_header(
        "GET",
        "api/cpgCouponPatPayGpiDtl/exportCsv",
        null,
        queryParams,
        "blob"
      );
      downloadCSV(response);
    } catch (error) {
      console.error("Error exporting as CSV:", error);
      toast.current.show({
        severity: "error",
        summary: "",
        detail: "Failed to export CSV. Please try again.",
        life: 3000,
      });
    }
  };

  const updateParamData = (data) => {
    setFirst(0);
    setCurrentPage(0);
    setParamsData(data);
  };

  const openInactiveConfirmationModal = () => {
    setIsInactiveConfirmationModalOpen(true);
  };

  const closeInactiveConfirmationModal = () => {
    console.log(inactiveSelectedOption);
    setIsInactiveConfirmationModalOpen(false);
    setInactiveSelectedOption("no");
  };

  const inactivateRecord = () => {
    if (inactiveSelectedOption === "yes") {
      const { cpfGpiListName } = paramsData;
      const { cpgGenericProductId } = rowData;

      const params = {
        screenNumber
      };

      let payload = [
        {
          cpfGpiListName: cpfGpiListName,
          cpgGenericProductId: cpgGenericProductId,
          cpgSequence: rowData[parmKey2],
        },
      ];

      if (selectedRows.length) {
        payload = selectedRows.map((item) => ({
          cpfGpiListName,
          cpgGenericProductId: item?.cpgGenericProductId,
          cpgSequence: item[parmKey2],
        }));
      }

      axios_helper_no_header(
        "PUT",
        "api/cpgCouponPatPayGpiDtl/inactivateCpgCouponPatpayGpiDtl",
        payload,
        params
      )
        .then((response) => {
          toast.current.show({
            severity: "success",
            summary: "",
            detail: response.responseStatus.message,
            life: 3000,
          });
          closeInactiveConfirmationModal();
        })
        .catch((error) => {
          console.error("There was an error inactivating the data!", error);
          const {
            responseStatus: { displayErrorFields = null, message } = {},
          } = error.data;
          toast.current.show({
            severity: "error",
            summary: "",
            detail: message,
            life: 3000,
          });
          closeInactiveConfirmationModal();
        }).finally(() => {
          fetchData();
        });
    } else {
      closeInactiveConfirmationModal();
    }
  };

  const editValidation = () => {
    const { cpfGpiListName } = paramsData;
    const { cpgGenericProductId } = rowData;
    const params = {
      cpfGpiListName: cpfGpiListName,
      cpgGenericProductId: cpgGenericProductId,
      cpgSequence: rowData[parmKey2],
    };
    axios_helper_no_header(
      "POST",
      "api/cpgCouponPatPayGpiDtl/validateBeforeEdit",
      null,
      params
    )
      .then((response) => {
        const steps = getSelectedSteps(selectedRows, rowData);
        navigateToPage(EDIT_COUPON_PATIENT_PAY_GPI_LIST, [
          ...previousState,
          {
            ...currentState,
            currentIndex,
            paramsData: {
              ...paramsData,
              cpgGenericProductId: steps.at(-1).cpgGenericProductId,
            }
          },
          {
            selectedSteps: steps,
            selectedSchedules: [paramsData],
            previousScreen: location.pathname,
            currentIndex: 0,
          },
        ]);
      })
      .catch((error) => {
        console.error("There was an error editing the data!", error);
        const { responseStatus: { displayErrorFields = null, message } = {} } =
          error.data;
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      });
  };

  const handleMessage = (event) => {
    const { type, data } = event.data;
    console.log("iframe load", type, data);
    if (type === "set-params") {
      const { currentIndex = 0, selectedSchedules = [] } = data[data.length - 1] || {};
      setStateVal(data);
      setExternalMode(true);
      if (selectedSchedules.length) {
       setParamsData(prev => ({
          ...prev,
          cpfGpiListName: selectedSchedules?.[currentIndex]?.cpfGpiListName,
          cpfGpiListDescription: selectedSchedules?.[currentIndex]?.cpfGpiListDescription,
        }));
      }
    }
    console.log("message", event.data);
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  });

  return {
    screenNumber,
    screen_title,
    formFields,
    dropdownValues,
    paramsData,
    updateParamData,
    ctaButtons,
    leftButtons,
    data,
    parmKey,
    menuOptions,
    columnDef,
    setRowData,
    selectedRows,
    setSelectedRows,
    first,
    rows,
    totalRecords,
    currentPage,
    onPageChange,
    isConfirmationModalOpen,
    closeConfirmationModal,
    setSelectedOption,
    inactivateRecord,
    isInactiveConfirmationModalOpen,
    closeInactiveConfirmationModal,
    setInactiveSelectedOption,
  };
};

export default useGPICouponPatientPay;